import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

const STORAGE_KEY =
  'weconnect_historico_filtros_v1';

const DEFAULT_FILTERS = {
  fecha_campo: 'updated_at',
  fecha_desde: '',
  fecha_hasta: '',
  nombre: '',
  telefono: '',
  hito: '',
  area: '',
  estado: '',
  scope: 'cerradas',
  motivo_cierre: '',
  agente_id: '',
  origen: '',
  prioridad: '',
  ia_activa: '',
  derivada_ia: '',
  mensaje: '',
  sort_by: 'updated_at',
  sort_dir: 'desc'
};

const AREA_LABELS = {
  ventas: 'Ventas',
  atencion: 'Atención',
  soporte: 'Soporte',
  marketing: 'Marketing'
};

function leerFiltrosGuardados() {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '{}'
    );

    return {
      ...DEFAULT_FILTERS,
      ...parsed
    };
  } catch {
    return { ...DEFAULT_FILTERS };
  }
}

function fechaLocalArgentina(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat(
    'es-AR',
    {
      timeZone:
        'America/Argentina/Buenos_Aires',
      dateStyle: 'short',
      timeStyle: 'short'
    }
  ).format(date);
}

function fechaIsoLocal(date) {
  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, '0');
  const day = String(
    date.getDate()
  ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function label(value) {
  if (!value) {
    return '-';
  }

  return String(value)
    .replace(/_dash$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char =>
      char.toUpperCase()
    );
}

function buildParams(
  filters,
  page,
  pageSize
) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(
    ([key, value]) => {
      if (
        value !== '' &&
        value !== null &&
        value !== undefined
      ) {
        params.set(key, String(value));
      }
    }
  );

  params.set('page', String(page));
  params.set(
    'page_size',
    String(pageSize)
  );

  return params;
}

function Badge({
  children,
  tone = 'neutral'
}) {
  return (
    <span
      className={`history-badge history-badge-${tone}`}
    >
      {children}
    </span>
  );
}

function HistoricoPanel({
  authFetch,
  apiUrl,
  agentes = [],
  areasHabilitadas = []
}) {
  const initialFilters = useMemo(
    leerFiltrosGuardados,
    []
  );

  const [draftFilters, setDraftFilters] =
    useState(initialFilters);

  const [
    appliedFilters,
    setAppliedFilters
  ] = useState(initialFilters);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState(50);

  const [result, setResult] = useState({
    resultados: [],
    total: 0,
    total_pages: 1,
    page: 1,
    page_size: 50,
    message_search: null
  });

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [exporting, setExporting] =
    useState(false);

  const [detail, setDetail] =
    useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [detailError, setDetailError] =
    useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const params = buildParams(
          appliedFilters,
          page,
          pageSize
        );

        const response = await authFetch(
          `${apiUrl}/api/dashboard/` +
          `conversaciones/historial?` +
          params.toString()
        );

        const data = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.error ||
            'No se pudo consultar el historial'
          );
        }

        if (!cancelled) {
          setResult({
            resultados:
              Array.isArray(data.resultados)
                ? data.resultados
                : [],
            total:
              Number(data.total || 0),
            total_pages:
              Number(data.total_pages || 1),
            page:
              Number(data.page || page),
            page_size:
              Number(
                data.page_size || pageSize
              ),
            message_search:
              data.message_search || null
          });
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
          setResult(previous => ({
            ...previous,
            resultados: []
          }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    apiUrl,
    authFetch,
    appliedFilters,
    page,
    pageSize
  ]);

  const updateFilter = (
    key,
    value
  ) => {
    setDraftFilters(previous => ({
      ...previous,
      [key]: value
    }));
  };

  const aplicar = () => {
    const next = {
      ...draftFilters
    };

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(next)
    );

    setPage(1);
    setAppliedFilters(next);
  };

  const limpiar = () => {
    const clean = {
      ...DEFAULT_FILTERS
    };

    localStorage.removeItem(
      STORAGE_KEY
    );

    setDraftFilters(clean);
    setAppliedFilters(clean);
    setPage(1);
  };

  const aplicarRapido = next => {
    setDraftFilters(next);
    setAppliedFilters(next);
    setPage(1);

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(next)
    );
  };

  const aplicarRango = days => {
    const end = new Date();
    const start = new Date();

    start.setDate(
      end.getDate() - Math.max(
        0,
        days - 1
      )
    );

    const next = {
      ...draftFilters,
      fecha_campo: 'updated_at',
      fecha_desde:
        fechaIsoLocal(start),
      fecha_hasta:
        fechaIsoLocal(end)
    };

    aplicarRapido(next);
  };

  const aplicarHoy = offset => {
    const date = new Date();

    date.setDate(
      date.getDate() + offset
    );

    const value = fechaIsoLocal(date);

    const next = {
      ...draftFilters,
      fecha_campo: 'updated_at',
      fecha_desde: value,
      fecha_hasta: value
    };

    setDraftFilters(next);
    setAppliedFilters(next);
    setPage(1);

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(next)
    );
  };

  const abrirDetalle = async row => {
    setDetail({
      conversacion: row,
      mensajes: [],
      timeline: [],
      dashboard_eventos: [],
      conversaciones_previas: []
    });
    setDetailLoading(true);
    setDetailError('');

    try {
      const response = await authFetch(
        `${apiUrl}/api/dashboard/` +
        `conversacion/${row.id}`
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
          'No se pudo abrir la conversación'
        );
      }

      setDetail(data);
    } catch (openError) {
      setDetailError(
        openError.message
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const exportar = async () => {
    setExporting(true);
    setError('');

    try {
      const params = buildParams(
        appliedFilters,
        1,
        500
      );

      params.delete('page');
      params.delete('page_size');

      const response = await authFetch(
        `${apiUrl}/api/dashboard/` +
        `conversaciones/historial/` +
        `exportar.csv?` +
        params.toString()
      );

      if (!response.ok) {
        const data = await response
          .json()
          .catch(() => ({}));

        throw new Error(
          data.error ||
          'No se pudo exportar el historial'
        );
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor =
        document.createElement('a');

      anchor.href = url;
      anchor.download =
        `weconnect-historial-` +
        `${fechaIsoLocal(new Date())}.csv`;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount =
    Object.entries(appliedFilters)
      .filter(([key, value]) => {
        if (
          key === 'scope' &&
          value === 'cerradas'
        ) {
          return false;
        }

        if (
          key === 'fecha_campo' &&
          value === 'updated_at'
        ) {
          return false;
        }

        if (
          key === 'sort_by' &&
          value === 'updated_at'
        ) {
          return false;
        }

        if (
          key === 'sort_dir' &&
          value === 'desc'
        ) {
          return false;
        }

        return value !== '';
      }).length;

  const totalPages = Math.max(
    1,
    Number(result.total_pages || 1)
  );

  return (
    <section
      className="history-panel"
      data-feature="HISTORICO_CONVERSACIONES_V1_2"
    >
      <header className="history-header">
        <div>
          <h1>Histórico de conversaciones</h1>
          <p>
            Búsqueda completa por fecha,
            cliente, teléfono, hito,
            estado, agente y mensajes.
          </p>
        </div>

        <div className="history-header-actions">
          <span className="history-total">
            {result.total.toLocaleString(
              'es-AR'
            )}{' '}
            conversaciones
          </span>

          <button
            type="button"
            className="history-button secondary"
            onClick={exportar}
            disabled={
              exporting ||
              loading
            }
          >
            {exporting
              ? 'Exportando…'
              : 'Exportar CSV'}
          </button>
        </div>
      </header>

      <div className="history-quick-filters">
        <span>Accesos rápidos:</span>

        <button
          type="button"
          onClick={() => aplicarHoy(0)}
        >
          Hoy
        </button>

        <button
          type="button"
          onClick={() => aplicarHoy(-1)}
        >
          Ayer
        </button>

        <button
          type="button"
          onClick={() => aplicarRango(7)}
        >
          7 días
        </button>

        <button
          type="button"
          onClick={() => aplicarRango(30)}
        >
          30 días
        </button>

        <button
          type="button"
          onClick={() =>
            aplicarRapido({
              ...draftFilters,
              scope: 'abiertas'
            })
          }
        >
          Abiertas
        </button>

        <button
          type="button"
          onClick={() =>
            aplicarRapido({
              ...draftFilters,
              scope: 'cerradas'
            })
          }
        >
          Cerradas
        </button>

        <button
          type="button"
          onClick={() =>
            aplicarRapido({
              ...draftFilters,
              hito: '',
              origen: 'outbound',
              scope: 'todas'
            })
          }
        >
          Outbound
        </button>
      </div>

      <div className="history-filters">
        <div className="history-field">
          <label>Fecha a consultar</label>
          <select
            value={
              draftFilters.fecha_campo
            }
            onChange={event =>
              updateFilter(
                'fecha_campo',
                event.target.value
              )
            }
          >
            <option value="updated_at">
              Última actividad
            </option>
            <option value="created_at">
              Creación
            </option>
            <option value="cerrada_at">
              Cierre
            </option>
          </select>
        </div>

        <div className="history-field">
          <label>Desde</label>
          <input
            type="date"
            value={
              draftFilters.fecha_desde
            }
            onChange={event =>
              updateFilter(
                'fecha_desde',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Hasta</label>
          <input
            type="date"
            value={
              draftFilters.fecha_hasta
            }
            onChange={event =>
              updateFilter(
                'fecha_hasta',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Nombre</label>
          <input
            type="text"
            placeholder="Nombre o apellido"
            value={draftFilters.nombre}
            onChange={event =>
              updateFilter(
                'nombre',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Teléfono</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Número completo o parcial"
            value={draftFilters.telefono}
            onChange={event =>
              updateFilter(
                'telefono',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Hito outbound</label>
          <input
            type="text"
            placeholder="Ej. hito_8"
            value={draftFilters.hito}
            onChange={event =>
              updateFilter(
                'hito',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Área</label>
          <select
            value={draftFilters.area}
            onChange={event =>
              updateFilter(
                'area',
                event.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>

            {areasHabilitadas.map(area => (
              <option
                key={area}
                value={area}
              >
                {AREA_LABELS[area] || area}
              </option>
            ))}
          </select>
        </div>

        <div className="history-field">
          <label>Abierta / cerrada</label>
          <select
            value={draftFilters.scope}
            onChange={event =>
              updateFilter(
                'scope',
                event.target.value
              )
            }
          >
            <option value="todas">
              Todas
            </option>
            <option value="abiertas">
              Abiertas
            </option>
            <option value="cerradas">
              Cerradas
            </option>
          </select>
        </div>

        <div className="history-field">
          <label>Estado exacto</label>
          <input
            type="text"
            placeholder="Ej. cerrada, pendiente"
            value={draftFilters.estado}
            onChange={event =>
              updateFilter(
                'estado',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Motivo de cierre</label>
          <input
            type="text"
            placeholder="Ej. venta_confirmada"
            value={
              draftFilters.motivo_cierre
            }
            onChange={event =>
              updateFilter(
                'motivo_cierre',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Agente</label>
          <select
            value={
              draftFilters.agente_id
            }
            onChange={event =>
              updateFilter(
                'agente_id',
                event.target.value
              )
            }
          >
            <option value="">
              Todos
            </option>

            {agentes.map(agent => (
              <option
                key={agent.id}
                value={agent.id}
              >
                {agent.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="history-field">
          <label>Origen</label>
          <input
            type="text"
            placeholder="Ej. outbound, landing"
            value={draftFilters.origen}
            onChange={event =>
              updateFilter(
                'origen',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Prioridad</label>
          <select
            value={
              draftFilters.prioridad
            }
            onChange={event =>
              updateFilter(
                'prioridad',
                event.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>
            <option value="normal">
              Normal
            </option>
            <option value="alta">
              Alta
            </option>
            <option value="critica">
              Crítica
            </option>
          </select>
        </div>

        <div className="history-field">
          <label>IA activa</label>
          <select
            value={
              draftFilters.ia_activa
            }
            onChange={event =>
              updateFilter(
                'ia_activa',
                event.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>
            <option value="true">
              Sí
            </option>
            <option value="false">
              No
            </option>
          </select>
        </div>

        <div className="history-field">
          <label>Derivada por IA</label>
          <select
            value={
              draftFilters.derivada_ia
            }
            onChange={event =>
              updateFilter(
                'derivada_ia',
                event.target.value
              )
            }
          >
            <option value="">
              Todas
            </option>
            <option value="true">
              Sí
            </option>
            <option value="false">
              No
            </option>
          </select>
        </div>

        <div className="history-field wide">
          <label>
            Texto dentro de mensajes
          </label>
          <input
            type="text"
            minLength={3}
            placeholder={
              'Mínimo 3 caracteres'
            }
            value={draftFilters.mensaje}
            onChange={event =>
              updateFilter(
                'mensaje',
                event.target.value
              )
            }
          />
        </div>

        <div className="history-field">
          <label>Orden</label>
          <select
            value={
              draftFilters.sort_by
            }
            onChange={event =>
              updateFilter(
                'sort_by',
                event.target.value
              )
            }
          >
            <option value="updated_at">
              Última actividad
            </option>
            <option value="created_at">
              Creación
            </option>
            <option value="cerrada_at">
              Cierre
            </option>
            <option value="nombre_cliente">
              Nombre
            </option>
            <option value="estado">
              Estado
            </option>
            <option value="area">
              Área
            </option>
          </select>
        </div>

        <div className="history-field">
          <label>Dirección</label>
          <select
            value={
              draftFilters.sort_dir
            }
            onChange={event =>
              updateFilter(
                'sort_dir',
                event.target.value
              )
            }
          >
            <option value="desc">
              Más reciente primero
            </option>
            <option value="asc">
              Más antiguo primero
            </option>
          </select>
        </div>

        <div className="history-filter-actions">
          <button
            type="button"
            className="history-button primary"
            onClick={aplicar}
            disabled={
              draftFilters.mensaje.length > 0 &&
              draftFilters.mensaje.length < 3
            }
          >
            Buscar
          </button>

          <button
            type="button"
            className="history-button secondary"
            onClick={limpiar}
          >
            Limpiar
          </button>
        </div>
      </div>

      <div className="history-summary">
        <span>
          {activeFilterCount
            ? `${activeFilterCount} filtros aplicados`
            : 'Filtro base: conversaciones cerradas'}
        </span>

        {result.message_search && (
          <span>
            Búsqueda en mensajes:{' '}
            {
              result.message_search
                .unique_conversations
            }{' '}
            conversaciones
            {result.message_search.truncated
              ? ' (resultado acotado)'
              : ''}
          </span>
        )}

        <label>
          Filas
          <select
            value={pageSize}
            onChange={event => {
              setPageSize(
                Number(event.target.value)
              );
              setPage(1);
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="history-error">
          {error}
        </div>
      )}

      <div className="history-table-wrap">
        <table className="history-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Área</th>
              <th>Estado</th>
              <th>Hito</th>
              <th>Agente</th>
              <th>Origen</th>
              <th>Motivo</th>
              <th>Último mensaje</th>
              <th />
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="history-empty"
                >
                  Consultando historial…
                </td>
              </tr>
            ) : result.resultados.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="history-empty"
                >
                  No hay conversaciones para
                  los filtros aplicados.
                </td>
              </tr>
            ) : (
              result.resultados.map(row => (
                <tr key={row.id}>
                  <td className="history-date">
                    {fechaLocalArgentina(
                      row.cerrada_at ||
                      row.updated_at ||
                      row.created_at
                    )}
                  </td>

                  <td>
                    <strong>
                      {row.nombre_cliente ||
                        'Sin nombre'}
                    </strong>
                    <small>
                      {row.telefono || '-'}
                    </small>
                  </td>

                  <td>
                    <Badge tone="blue">
                      {AREA_LABELS[
                        row.area ||
                        row.categoria
                      ] ||
                        row.area ||
                        row.categoria ||
                        '-'}
                    </Badge>
                  </td>

                  <td>
                    <Badge
                      tone={
                        row.estado === 'cerrada'
                          ? 'neutral'
                          : row.estado === 'escalada'
                          ? 'amber'
                          : 'green'
                      }
                    >
                      {label(row.estado)}
                    </Badge>
                  </td>

                  <td>
                    {row.hito_outbound ? (
                      <Badge tone="purple">
                        {label(
                          row.hito_outbound
                        )}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </td>

                  <td>
                    {row.agente_nombre ||
                      'Sin asignar'}
                  </td>

                  <td>
                    {label(row.origen)}
                  </td>

                  <td>
                    {label(
                      row.motivo_cierre
                    )}
                  </td>

                  <td
                    className="history-message-cell"
                    title={
                      row.mensaje_coincidente ||
                      row.ultimo_mensaje ||
                      ''
                    }
                  >
                    {row.mensaje_coincidente ? (
                      <>
                        <Badge tone="amber">
                          Coincidencia
                        </Badge>
                        <span>
                          {
                            row.mensaje_coincidente
                          }
                        </span>
                      </>
                    ) : (
                      row.ultimo_mensaje ||
                      'Sin mensajes'
                    )}
                  </td>

                  <td>
                    <button
                      type="button"
                      className="history-open-button"
                      onClick={() =>
                        abrirDetalle(row)
                      }
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className="history-pagination">
        <button
          type="button"
          disabled={page <= 1 || loading}
          onClick={() =>
            setPage(current =>
              Math.max(1, current - 1)
            )
          }
        >
          Anterior
        </button>

        <span>
          Página {page} de {totalPages}
        </span>

        <button
          type="button"
          disabled={
            page >= totalPages ||
            loading
          }
          onClick={() =>
            setPage(current =>
              Math.min(
                totalPages,
                current + 1
              )
            )
          }
        >
          Siguiente
        </button>
      </footer>

      {detail && (
        <div
          className="history-detail-overlay"
          onMouseDown={event => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setDetail(null);
            }
          }}
        >
          <aside className="history-detail">
            <header>
              <div>
                <h2>
                  {
                    detail.conversacion
                      ?.nombre_cliente ||
                    'Sin nombre'
                  }
                </h2>
                <p>
                  {
                    detail.conversacion
                      ?.telefono ||
                    '-'
                  }
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setDetail(null)
                }
                aria-label="Cerrar detalle"
              >
                ×
              </button>
            </header>

            <div className="history-detail-meta">
              <div>
                <span>Estado</span>
                <strong>
                  {label(
                    detail.conversacion
                      ?.estado
                  )}
                </strong>
              </div>

              <div>
                <span>Área</span>
                <strong>
                  {AREA_LABELS[
                    detail.conversacion
                      ?.area ||
                    detail.conversacion
                      ?.categoria
                  ] ||
                    detail.conversacion
                      ?.area ||
                    detail.conversacion
                      ?.categoria ||
                    '-'}
                </strong>
              </div>

              <div>
                <span>Hito</span>
                <strong>
                  {label(
                    detail.conversacion
                      ?.hito_outbound
                  )}
                </strong>
              </div>

              <div>
                <span>Motivo</span>
                <strong>
                  {label(
                    detail.conversacion
                      ?.motivo_cierre
                  )}
                </strong>
              </div>

              <div>
                <span>Agente</span>
                <strong>
                  {
                    detail.conversacion
                      ?.agente_nombre ||
                    'Sin asignar'
                  }
                </strong>
              </div>

              <div>
                <span>Fecha</span>
                <strong>
                  {fechaLocalArgentina(
                    detail.conversacion
                      ?.cerrada_at ||
                    detail.conversacion
                      ?.updated_at ||
                    detail.conversacion
                      ?.created_at
                  )}
                </strong>
              </div>
            </div>

            {detailError && (
              <div className="history-error">
                {detailError}
              </div>
            )}

            <div className="history-detail-section">
              <h3>Mensajes</h3>

              {detailLoading ? (
                <div className="history-empty">
                  Cargando conversación…
                </div>
              ) : (
                <div className="history-message-list">
                  {(
                    detail.mensajes || []
                  ).length === 0 ? (
                    <div className="history-empty">
                      Sin mensajes.
                    </div>
                  ) : (
                    detail.mensajes.map(
                      (message, index) => (
                        <article
                          key={
                            message.id ||
                            `${message.created_at}-${index}`
                          }
                          className={
                            `history-message ` +
                            `history-message-` +
                            `${message.rol || 'sistema'}`
                          }
                        >
                          <header>
                            <strong>
                              {label(
                                message.rol
                              )}
                            </strong>
                            <span>
                              {fechaLocalArgentina(
                                message.created_at
                              )}
                            </span>
                          </header>

                          <p>
                            {message.contenido ||
                              '[Mensaje sin texto]'}
                          </p>
                        </article>
                      )
                    )
                  )}
                </div>
              )}
            </div>

            <div className="history-detail-section">
              <h3>Timeline</h3>

              <div className="history-timeline">
                {[
                  ...(detail.timeline || []),
                  ...(
                    detail.dashboard_eventos ||
                    []
                  )
                ]
                  .sort(
                    (a, b) =>
                      new Date(
                        a.created_at || 0
                      ) -
                      new Date(
                        b.created_at || 0
                      )
                  )
                  .map((event, index) => (
                    <div
                      key={
                        event.id ||
                        `${event.created_at}-${index}`
                      }
                      className="history-timeline-item"
                    >
                      <strong>
                        {label(
                          event.tipo ||
                          event.tipo_evento
                        )}
                      </strong>
                      <span>
                        {fechaLocalArgentina(
                          event.created_at
                        )}
                      </span>
                      {event.observacion && (
                        <p>
                          {event.observacion}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

export default React.memo(
  HistoricoPanel
);
